/**
 * Manufacturing management → Work orders (master–detail).
 *
 * List on the left, work order detail on the right — same pattern as batch details and purchase invoices.
 * Deep link: `/manufacturing-management/work-orders/:buildOrderId` (list + detail panel).
 *
 * Supabase-first replacement for the Appsmith Work Orders page. The list is fetched from
 * /api/manufacturing/work-orders (no Megaventory call on load); destructive actions fan out to
 * Megaventory through dedicated server endpoints that immediately mirror the response onto
 * `public.build_order` so `mv_id` and `mv_number` stay consistent after BOM rebuilds. Hub-initiated BOM rebuild runs
 * immediately; the rebuild queue holds connector-submitted drafts until someone approves them.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ClipboardList,
  Plus,
  Search,
  X,
} from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { OperationalLocationNativeSelectOptions } from "@/components/mrp/TransferEndpointLocationSelect";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { buildManufacturingWorkOrderDetailPath } from "@/lib/hubEntityDetailPaths";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import {
  useOperationalLocations,
  type TransferEndpointLocationRow,
} from "@/lib/transferEndpointLocations";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { WorkOrderDetailWorkspace } from "./workOrderDetailWorkspace";
import {
  DEFAULT_LIMIT,
  MANUFACTURING_HUB_READ_ONLY,
  STATUS_OPTIONS,
  WORK_ORDERS_PAGE_HELP,
  buildLegacyWorkOrderIdRedirectTarget,
  buildQueryParams,
  formatCount,
  formatDateTime,
  type LocationFilter,
  type RebuildRequestSummary,
  type StatusFilter,
  resolveWorkOrderSelectedBuildOrderId,
  type WorkOrderDetail,
  type WorkOrderListResponse,
  type WorkOrderListRow,
  effectiveWorkOrderStatus,
  workOrderPrimaryLabel,
} from "./workOrdersPageModel";
import { StatusPill, SummaryCard } from "./workOrdersPageUi";

interface CreateFormState {
  sku: string;
  quantity: string;
  locationId: string;
  manufacturerReferenceNumber: string;
  comments: string;
  stocking: boolean;
}

const DEFAULT_CREATE_STATE: CreateFormState = {
  sku: "",
  quantity: "1",
  locationId: "",
  manufacturerReferenceNumber: "",
  comments: "",
  stocking: true,
};

type WorkOrderListSortKey = "number" | "sku" | "status" | "location";

const WORK_ORDER_LIST_COLUMNS: HubDataTableColumn<WorkOrderListRow, WorkOrderListSortKey>[] = [
  {
    id: "number",
    header: "Number",
    sortKey: "number",
    overflow: "nowrap",
    sortValue: (row) => workOrderPrimaryLabel(row),
    cell: (row) => <span className="font-mono text-xs">{row.number}</span>,
  },
  {
    id: "sku",
    header: "Finished good",
    sortKey: "sku",
    overflow: "nowrap",
    sortValue: (row) => row.sku ?? "",
    cell: (row) => <span className="font-mono text-xs">{row.sku ?? "—"}</span>,
  },
  {
    id: "status",
    header: "Status",
    sortKey: "status",
    sortValue: (row) => effectiveWorkOrderStatus(row),
    cell: (row) => <StatusPill row={row} />,
  },
  {
    id: "location",
    header: "Location",
    sortKey: "location",
    sortValue: (row) => row.location_name ?? "",
    cell: (row) =>
      row.location_id != null ? (
        <ManufacturingLocationChip locationId={row.location_id} fallbackLabel={row.location_name ?? undefined} />
      ) : (
        <span className="text-slate-400">—</span>
      ),
  },
];

export function WorkOrdersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { buildOrderId: pathBuildOrderId } = useParams<{ buildOrderId?: string }>();
  const [searchParams] = useSearchParams();
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const operationalLocations = useOperationalLocations();
  const [status, setStatus] = useState<StatusFilter>("active");
  const [location, setLocation] = useState<LocationFilter>("any");
  const [hideGso, setHideGso] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(DEFAULT_CREATE_STATE);
  const [rebuildQueueModalOpen, setRebuildQueueModalOpen] = useState(false);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [shipmentFilterId, setShipmentFilterId] = useState<number | null>(null);
  const selectedId = useMemo(
    () => resolveWorkOrderSelectedBuildOrderId(pathBuildOrderId, searchParams.get("id")),
    [pathBuildOrderId, searchParams],
  );

  const openWorkOrder = useCallback(
    (buildOrderId: number) => {
      navigate(buildManufacturingWorkOrderDetailPath(buildOrderId));
    },
    [navigate],
  );

  const closeWorkOrder = useCallback(() => {
    navigate("/manufacturing-management/work-orders", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (pathBuildOrderId != null) {
      return;
    }
    const target = buildLegacyWorkOrderIdRedirectTarget(searchParams);
    if (target != null) {
      navigate(target, { replace: true });
    }
  }, [navigate, pathBuildOrderId, searchParams]);

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const idRaw = searchParams.get("id")?.trim() ?? "";
    const numberRaw = searchParams.get("number")?.trim() ?? "";
    const hideGsoRaw = searchParams.get("hide_gso")?.trim() ?? "";
    const shipmentRaw = searchParams.get("shipment_id")?.trim() ?? "";
    if (shipmentRaw.length > 0) {
      const shipmentId = Number(shipmentRaw);
      if (Number.isFinite(shipmentId) && shipmentId > 0) {
        setShipmentFilterId(shipmentId);
        setStatus("all");
        setHideGso(false);
      }
    } else {
      setShipmentFilterId(null);
    }
    if (hideGsoRaw === "0" || idRaw.length > 0 || numberRaw.length > 0 || shipmentRaw.length > 0) {
      setHideGso(false);
    } else if (hideGsoRaw === "1") {
      setHideGso(true);
    }
    if (idRaw.length > 0) {
      const id = Number(idRaw);
      if (Number.isFinite(id) && id > 0) {
        setStatus("all");
      }
    }
    if (numberRaw.length > 0) {
      setSearchInput(numberRaw);
      setSearch(numberRaw);
      setStatus("all");
    }
  }, [searchParams]);

  const listQueryKey = ["manufacturing", "work-orders", status, location, hideGso, search, limit, shipmentFilterId] as const;
  const listQuery = useQuery<WorkOrderListResponse, ApiError>({
    queryKey: listQueryKey,
    queryFn: () =>
      apiFetch<WorkOrderListResponse>("/api/manufacturing/work-orders", {
        query: { ...buildQueryParams(status, location, hideGso, search, limit, shipmentFilterId), background: "1" },
      }),
    staleTime: 60_000,
  });

  useHubCacheStream({
    channels: [HUB_CACHE_CHANNEL.manufacturingWorkOrdersList],
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: listQueryKey });
      }
    },
  });

  useEffect(() => {
    const numberRaw = searchParams.get("number")?.trim() ?? "";
    if (numberRaw.length === 0 || listQuery.data == null) return;
    const needle = numberRaw.toLowerCase();
    const match = listQuery.data.workOrders.find((row) => {
      const internal = row.number.trim().toLowerCase();
      const mv = (row.mv_number ?? "").trim().toLowerCase();
      return internal === needle || mv === needle;
    });
    if (match != null && selectedId !== match.id) {
      openWorkOrder(match.id);
    }
  }, [listQuery.data, openWorkOrder, searchParams, selectedId]);

  useEffect(() => {
    const idRaw = searchParams.get("id")?.trim() ?? "";
    const numberRaw = searchParams.get("number")?.trim() ?? "";
    if (idRaw.length === 0 || numberRaw.length > 0 || listQuery.data == null) return;
    const id = Number(idRaw);
    if (!Number.isFinite(id) || id <= 0) return;
    const match = listQuery.data.workOrders.find((row) => row.id === id);
    if (match == null) return;
    const label = (match.mv_number ?? match.number).trim();
    if (label.length === 0) return;
    setSearchInput(label);
    setSearch(label);
  }, [listQuery.data, searchParams]);

  function handleSelect(id: number): void {
    if (selectedId === id) {
      closeWorkOrder();
      return;
    }
    openWorkOrder(id);
  }

  function refreshAll(): void {
    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders"] });
  }

  const createMutation = useMutation<WorkOrderDetail, ApiError, CreateFormState>({
    mutationFn: (form) =>
      apiFetch<WorkOrderDetail>("/api/manufacturing/work-orders", {
        method: "POST",
        body: {
          sku: form.sku.trim(),
          quantity: Number(form.quantity),
          locationId: Number(form.locationId),
          manufacturerReferenceNumber: form.manufacturerReferenceNumber.trim(),
          comments: form.comments.trim() === "" ? null : form.comments.trim(),
          stocking: form.stocking,
        },
      }),
    onSuccess: (created) => {
      setCreateOpen(false);
      setCreateForm(DEFAULT_CREATE_STATE);
      openWorkOrder(created.id);
      refreshAll();
    },
  });

  const rebuildQueueListQuery = useQuery<{ requests: RebuildRequestSummary[] }, ApiError>({
    queryKey: ["manufacturing", "work-order-rebuild-requests", "pending-mcp-all"],
    queryFn: () =>
      apiFetch<{ requests: RebuildRequestSummary[] }>("/api/manufacturing/work-order-rebuild-requests", {
        query: { status: "pending", source: "mcp" },
      }),
    staleTime: 10_000,
  });

  const pendingRebuildQueueCount = rebuildQueueListQuery.data?.requests.length ?? 0;

  const rows = listQuery.data?.workOrders ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">Work orders</h1>
            <HoverHintWithGlyph hoverHint={WORK_ORDERS_PAGE_HELP} size="md" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRebuildQueueModalOpen(true)}
            aria-label={
              rebuildQueueListQuery.data === undefined && rebuildQueueListQuery.isLoading
                ? "Connector BOM rebuild queue, loading count"
                : `Connector BOM rebuild queue, ${pendingRebuildQueueCount} pending`
            }
          >
            <ClipboardList className="h-4 w-4" aria-hidden />
            <span className="inline-flex items-center gap-2">
              Connector rebuild queue
              <Badge
                tone={
                  rebuildQueueListQuery.data === undefined && rebuildQueueListQuery.isLoading
                    ? "muted"
                    : pendingRebuildQueueCount > 0
                      ? "info"
                      : "muted"
                }
                className="min-w-[1.25rem] justify-center tabular-nums"
              >
                {rebuildQueueListQuery.data === undefined && rebuildQueueListQuery.isLoading
                  ? "…"
                  : pendingRebuildQueueCount}
              </Badge>
            </span>
          </Button>
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
            <Button
              onClick={() => {
                setCreateForm(DEFAULT_CREATE_STATE);
                setCreateOpen(true);
              }}
              disabled={!hubWritesAllowed}
            >
              <Plus className="h-4 w-4" aria-hidden /> New work order
            </Button>
          </ReadOnlyHoverLayer>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard
          label="Active work orders"
          value={
            listQuery.data != null ? formatCount(listQuery.data.summary.activeNotTerminalCount) : "—"
          }
          helper="Megaventory status not Closed/Completed/Cancelled (mirrored in Supabase). Count is over every row that matches Status, location, search, and Hide GSO — not just the table page."
        />
        <SummaryCard
          label="Pending"
          value={listQuery.data != null ? formatCount(listQuery.data.summary.pendingCount) : "—"}
          helper="Megaventory status Pending. Same full filtered count as above; often 0 until mv_status is populated by ingest."
        />
        <SummaryCard
          label="Receivable"
          value={listQuery.data != null ? formatCount(listQuery.data.summary.receivableCount) : "—"}
          helper="Past Pending with received under ordered (MV mirrored columns). Same full filtered count; needs mv_status and MV quantities on rows."
        />
        </div>
      </div>

      <Card>
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={status}
                onChange={(event) => setStatus(event.target.value as StatusFilter)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <select
                id="location"
                className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={location}
                onChange={(event) => setLocation(event.target.value as LocationFilter)}
              >
                <option value="any">All locations</option>
                <OperationalLocationNativeSelectOptions locations={operationalLocations.locations} />
              </select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
                <Input
                  id="search"
                  className="pl-8"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="MV number, internal number, SKU, MFR reference, or notes"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={hideGso}
                onChange={(event) => setHideGso(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Hide GSO references
            </label>
            {shipmentFilterId != null ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-800 ring-1 ring-sky-200">
                Shipment {shipmentFilterId}
                <Link to="/manufacturing-management/work-orders" className="text-sky-700 underline hover:text-sky-900">
                  Clear
                </Link>
              </span>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {listQuery.error ? (
        <Card>
          <CardBody className="flex items-start gap-3 text-sm text-red-700">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
            <div>
              <div className="font-semibold">Could not load work orders</div>
              <div>{listQuery.error.message}</div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
        <HubDataTable
          fillViewport={false}
          className="max-h-[68vh] min-h-[20rem]"
          title={
            listQuery.isLoading
              ? "Loading…"
              : listQuery.data && listQuery.data.totalRows > rows.length
                ? `Showing ${rows.length} of ${(listQuery.data.totalRows ?? 0).toLocaleString()} matches`
                : `${rows.length} matching`
          }
          headerExtra={
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {listQuery.data && listQuery.data.totalRows > rows.length ? (
                <button
                  type="button"
                  className="text-brand-600 hover:text-brand-700"
                  onClick={() => setLimit((current) => Math.min(current + 1000, 5000))}
                >
                  Show more
                </button>
              ) : null}
              {listQuery.data ? <span>Loaded {formatDateTime(listQuery.data.generatedAt)}</span> : null}
            </div>
          }
          columns={WORK_ORDER_LIST_COLUMNS}
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading}
          loadingMessage="Loading work orders…"
          empty="No work orders match these filters."
          isFiltered={status !== "active" || location !== "any" || !hideGso || search.trim() !== "" || shipmentFilterId != null}
          filteredEmpty="No work orders match these filters."
          onRowClick={(row) => handleSelect(row.id)}
          rowClassName={(row) =>
            selectedId === row.id ? "bg-brand-50/60 hover:bg-brand-50" : undefined
          }
        />

        <div className="flex min-h-0 min-w-0 flex-col gap-4">
          {selectedId == null ? (
            <Card className="flex min-h-0 flex-col">
              <CardBody className="flex flex-1 flex-col justify-center">
                <p className="text-sm text-slate-600">
                  Select a work order from the list to see BOM lines, allocate materials, receive finished goods, and
                  rebuild options.
                </p>
              </CardBody>
            </Card>
          ) : (
            <WorkOrderDetailWorkspace buildOrderId={selectedId} onClose={closeWorkOrder} />
          )}
        </div>
      </div>

      {createOpen ? (
        <CreateWorkOrderModal
          form={createForm}
          locations={operationalLocations.locations}
          locationsLoading={operationalLocations.isLoading}
          onChange={setCreateForm}
          onClose={() => {
            if (createMutation.isPending) return;
            setCreateOpen(false);
            setCreateForm(DEFAULT_CREATE_STATE);
          }}
          onSubmit={() => {
            if (createForm.locationId.trim() === "") return;
            createMutation.mutate(createForm);
          }}
          isSubmitting={createMutation.isPending}
          error={createMutation.error ?? null}
          hubWritesAllowed={hubWritesAllowed}
        />
      ) : null}

      <WorkOrderRebuildQueueModal
        open={rebuildQueueModalOpen}
        onClose={() => setRebuildQueueModalOpen(false)}
        requests={rebuildQueueListQuery.data?.requests ?? []}
        isLoading={rebuildQueueListQuery.isLoading}
        error={rebuildQueueListQuery.error ?? null}
        onOpenWorkOrder={(buildOrderId) => {
          openWorkOrder(buildOrderId);
          setRebuildQueueModalOpen(false);
        }}
      />
    </div>
  );
}

function formatRebuildQueueTimestamp(iso: string): string {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function WorkOrderRebuildQueueModal({
  open,
  onClose,
  requests,
  isLoading,
  error,
  onOpenWorkOrder,
}: {
  open: boolean;
  onClose: () => void;
  requests: RebuildRequestSummary[];
  isLoading: boolean;
  error: ApiError | null;
  onOpenWorkOrder: (buildOrderId: number) => void;
}): null | JSX.Element {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex min-h-0 items-start justify-center overflow-y-auto bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rebuild-queue-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Card
        className="relative z-[101] my-4 w-full max-w-5xl shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>
              <span id="rebuild-queue-modal-title">Pending connector BOM rebuilds</span>
            </CardTitle>
            <button
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close queue"
              type="button"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            These are draft rebuilds submitted by the manufacturing Model Context Protocol tools. Open a work order to
            approve or reject in the detail panel; approval runs Megaventory.
          </p>
        </CardHeader>
        <CardBody className="space-y-3">
          {isLoading ? <p className="text-sm text-slate-500">Loading queue…</p> : null}
          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              {error.message}
            </div>
          ) : null}
          {!isLoading && !error && requests.length === 0 ? (
            <p className="text-sm text-slate-600">No pending connector BOM rebuilds.</p>
          ) : null}
          {!isLoading && !error && requests.length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Request</th>
                    <th className="px-3 py-2 text-left font-semibold">Megaventory</th>
                    <th className="px-3 py-2 text-left font-semibold">Finished good</th>
                    <th className="px-3 py-2 text-right font-semibold">Build order</th>
                    <th className="px-3 py-2 text-left font-semibold">Submitted</th>
                    <th className="px-3 py-2 text-left font-semibold">Expires</th>
                    <th className="px-3 py-2 text-left font-semibold">Source</th>
                    <th className="px-3 py-2 text-left font-semibold">Note</th>
                    <th className="px-3 py-2 text-right font-semibold"> </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {requests.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 font-mono text-xs">{row.id}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.mv_number ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.sku ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{row.build_order_id}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">{formatRebuildQueueTimestamp(row.requested_at)}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">{formatRebuildQueueTimestamp(row.expires_at)}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">{row.source}</td>
                      <td className="max-w-[14rem] px-3 py-2 text-xs text-slate-600" title={row.reason ?? undefined}>
                        {row.reason ? (
                          <span className="line-clamp-2">{row.reason}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button type="button" variant="secondary" size="sm" onClick={() => onOpenWorkOrder(row.build_order_id)}>
                          Open work order
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <div className="flex justify-end border-t border-slate-100 pt-3">
            <Button type="button" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

function CreateWorkOrderModal({
  form,
  locations,
  locationsLoading,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
  error,
  hubWritesAllowed,
}: {
  form: CreateFormState;
  locations: TransferEndpointLocationRow[];
  locationsLoading: boolean;
  onChange: (value: CreateFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  error: ApiError | null;
  hubWritesAllowed: boolean;
}) {
  const locationSelected = form.locationId.trim() !== "";
  const skuSelected = form.sku.trim() !== "";
  const quantityValid = Number(form.quantity) > 0;
  const canSubmit = hubWritesAllowed && !isSubmitting && locationSelected && skuSelected && quantityValid;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex min-h-0 items-start justify-center overflow-y-auto bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>New work order</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div>
            <Label htmlFor="create-sku">Finished good SKU</Label>
            <AssemblySkuCombobox
              id="create-sku"
              value={form.sku}
              onChange={(sku) => onChange({ ...form, sku })}
              placeholder="for example FI-5A-W"
              className="w-full"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="create-quantity">Quantity</Label>
              <Input
                id="create-quantity"
                type="number"
                min="1"
                step="1"
                value={form.quantity}
                onChange={(event) => onChange({ ...form, quantity: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="create-location">Location</Label>
              <select
                id="create-location"
                className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={form.locationId}
                onChange={(event) => onChange({ ...form, locationId: event.target.value })}
                required
              >
                <option value="" disabled>
                  Select location
                </option>
                <OperationalLocationNativeSelectOptions locations={locations} />
              </select>
              {!locationSelected ? (
                <p className="mt-1 text-xs text-slate-600">
                  {locationsLoading
                    ? "Loading warehouses…"
                    : "Required. Choose any active warehouse before creating."}
                </p>
              ) : null}
            </div>
          </div>
          <div>
            <Label htmlFor="create-mfr-ref">Manufacturer reference number</Label>
            <Input
              id="create-mfr-ref"
              value={form.manufacturerReferenceNumber}
              onChange={(event) => onChange({ ...form, manufacturerReferenceNumber: event.target.value })}
              placeholder="for example GP01718 or sales order number"
            />
          </div>
          <div>
            <Label htmlFor="create-comments">Comments</Label>
            <TextArea
              id="create-comments"
              value={form.comments}
              onChange={(event) => onChange({ ...form, comments: event.target.value })}
              placeholder="Optional notes on this work order."
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.stocking}
              onChange={(event) => onChange({ ...form, stocking: event.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Stocking work order
          </label>
          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {error.message}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
              <Button onClick={onSubmit} isLoading={isSubmitting} disabled={!canSubmit}>
                Create work order
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

